import { Button, ButtonGroup, Grid, Heading } from '@design-systems/canvas-ui'
import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
    convertTimeDimensionToGFFormat,
    extractMetricValue,
    getColorClassName,
    getTimeDimensionTranslationKey,
    getMetricDisplayName,
    sortMetricsByColumnIndex,
    sortSectionsByColumnIndex,
} from './helpers'
import { adaptStatusColorToClass } from 'utils/style-adapters'
import { useLogClick } from 'utils/analytics'
import { MetricCard } from './metric-card'
import { Metric, EnrichedMetric } from './types'
import { useEssentials } from 'hooks/utils/useEssentials'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { TableData, Cell } from 'utils/data'
import { isStore } from 'utils/location'

interface PerformanceMetricCardsProps {
    data: Metric[]
    dataByTimeDimension: Record<string, TableData | undefined>
    goalsData: any
    tabName: string
}

export const PerformanceMetricCards = ({
    data,
    dataByTimeDimension,
    goalsData,
    tabName,
}: PerformanceMetricCardsProps) => {
    const { t, currentLocation } = useEssentials()
    const { logClick } = useLogClick('myTarget-performance')
    const { myTargetPerformance } = TRANSLATION_KEYS
    const { sections: sectionKeys } = myTargetPerformance
    const sectionTranslations: Record<string, string> = {
        Financial: sectionKeys.financial,
        'Guest Compliance': sectionKeys.guestCompliance,
        'Guest Engagement': sectionKeys.guestEngagement,
        'Guest Impact': sectionKeys.guestImpact,
        'Guest Readiness': sectionKeys.guestReadiness,
        'Guest Satisfaction': sectionKeys.guestSatisfaction,
        Team: sectionKeys.team,
    }
    const showSeeDetails = !isStore(currentLocation)
    const [searchParams, setSearchParams] = useSearchParams()
    const selectedType =
        (searchParams.get('type') as 'outcomes' | 'levers') ?? 'outcomes'

    // Extract greenfield headings once for column ordering
    const greenfieldHeadings = useMemo(() => {
        const firstAvailableData = Object.values(dataByTimeDimension)[0]
        return firstAvailableData?.headings
    }, [dataByTimeDimension])

    // Group metrics by section
    const metricsBySection = useMemo(() => {
        if (!data || !Array.isArray(data)) return {}

        const filtered = data.filter(
            (metric: Metric) => metric.metric_type === selectedType,
        )

        const grouped = filtered.reduce(
            (acc: Record<string, Metric[]>, metric: Metric) => {
                const section = metric.section || 'Other'
                if (!acc[section]) {
                    acc[section] = []
                }
                acc[section].push(metric)
                return acc
            },
            {},
        )

        // Sort metrics within each section by their column index in the greenfield card
        Object.keys(grouped).forEach((section) => {
            grouped[section] = sortMetricsByColumnIndex(
                grouped[section],
                greenfieldHeadings,
            )
        })

        return grouped
    }, [data, selectedType, greenfieldHeadings])

    // Sort sections by the minimum column index of metrics within each section
    const sections = useMemo(() => {
        const sectionNames = Object.keys(metricsBySection)

        return sortSectionsByColumnIndex(
            sectionNames,
            metricsBySection,
            greenfieldHeadings,
        )
    }, [metricsBySection, greenfieldHeadings])

    // Precompute all metric values to avoid recalculation during render
    const enrichedMetricsBySection = useMemo(() => {
        const enriched: Record<string, EnrichedMetric[]> = {}

        Object.keys(metricsBySection).forEach((section) => {
            enriched[section] = metricsBySection[section].map((metric) => {
                const primaryTimeDimension = convertTimeDimensionToGFFormat(
                    metric.primary_time_dimension,
                )
                const primaryDimensionData =
                    dataByTimeDimension[primaryTimeDimension]
                const metricValue = extractMetricValue(
                    primaryDimensionData,
                    metric.metric_name,
                )
                const colorClass = getColorClassName(metricValue?.color)
                const textColorClass = adaptStatusColorToClass(
                    metricValue?.color || '',
                )
                const displayName = getMetricDisplayName(
                    primaryDimensionData,
                    metric.metric_name,
                )
                const goalValue = extractMetricValue(goalsData, displayName)

                // Precompute historical values (exclude primary and secondary dimensions)
                const historicalValues = metric.time_dimensions
                    .filter(
                        (timeDim) =>
                            timeDim !== metric.primary_time_dimension &&
                            timeDim !== metric.secondary_time_dimension,
                    )
                    .map((timeDim) => {
                        const gfDim = convertTimeDimensionToGFFormat(timeDim)
                        const dimData = dataByTimeDimension[gfDim]
                        const value = extractMetricValue(
                            dimData,
                            metric.metric_name,
                        )
                        const colorClass = adaptStatusColorToClass(
                            value?.color || '',
                        )
                        const translationKey =
                            getTimeDimensionTranslationKey(timeDim)
                        return {
                            timeDim,
                            gfDim,
                            value,
                            colorClass,
                            translationKey,
                        }
                    })

                // Precompute secondary time dimension value (if exists)
                let secondaryTimeDimensionValue = {
                    timeDimension: metric.secondary_time_dimension,
                    value: null as Cell | null,
                    colorClass: '',
                }
                if (metric.secondary_time_dimension) {
                    const secondaryGfDim = convertTimeDimensionToGFFormat(
                        metric.secondary_time_dimension,
                    )
                    const secondaryData = dataByTimeDimension[secondaryGfDim]
                    const secondaryValue = extractMetricValue(
                        secondaryData,
                        metric.metric_name,
                    )
                    const secondaryColorClass = adaptStatusColorToClass(
                        secondaryValue?.color || '',
                    )
                    secondaryTimeDimensionValue = {
                        timeDimension: metric.secondary_time_dimension,
                        value: secondaryValue,
                        colorClass: secondaryColorClass,
                    }
                }

                return {
                    ...metric,
                    primaryTimeDimension,
                    primaryDimensionData,
                    metricValue,
                    colorClass,
                    textColorClass,
                    displayName,
                    goalValue,
                    historicalValues,
                    secondaryTimeDimensionValue,
                }
            })
        })

        return enriched
    }, [metricsBySection, dataByTimeDimension, goalsData])

    return (
        <>
            <Grid.Item
                size={{
                    xs: 12,
                }}
            >
                <Grid.Container
                    align="center"
                    justify="center"
                    className="hc8-pt-4x"
                >
                    <Grid.Item>
                        <ButtonGroup>
                            <Button
                                variant={
                                    selectedType === 'outcomes'
                                        ? 'primary'
                                        : 'secondary'
                                }
                                onClick={() => {
                                    logClick(`${tabName} outcomes click`)
                                    setSearchParams((prev) => {
                                        prev.set('type', 'outcomes')
                                        return prev
                                    })
                                }}
                            >
                                {t(myTargetPerformance.metricTypes.outcomes)}
                            </Button>
                            <Button
                                variant={
                                    selectedType === 'levers'
                                        ? 'primary'
                                        : 'secondary'
                                }
                                onClick={() => {
                                    logClick(`${tabName} levers click`)
                                    setSearchParams((prev) => {
                                        prev.set('type', 'levers')
                                        return prev
                                    })
                                }}
                            >
                                {t(myTargetPerformance.metricTypes.levers)}
                            </Button>
                        </ButtonGroup>
                    </Grid.Item>
                </Grid.Container>
            </Grid.Item>
            <Grid.Item
                size={{
                    xs: 12,
                }}
            >
                {sections.length === 0 ? (
                    <Grid.Container align="center" justify="center">
                        <Grid.Item>
                            {t(myTargetPerformance.noMetricsAvailable, {
                                metricType: t(
                                    myTargetPerformance.metricTypes[
                                        selectedType
                                    ],
                                ).toLowerCase(),
                            })}
                        </Grid.Item>
                    </Grid.Container>
                ) : (
                    sections.map((section) => (
                        <div key={section} className="hc8-mb-4x">
                            <Heading className="hc8-fs-200 hc8-mb-4x">
                                {t(sectionTranslations[section] ?? section)}
                            </Heading>
                            <div className="guest-metrics-scroll-container">
                                {enrichedMetricsBySection[section].map(
                                    (metric) => (
                                        <MetricCard
                                            key={metric.metric_id}
                                            metric={metric}
                                            showSeeDetails={showSeeDetails}
                                            tabName={tabName}
                                            onHistoricalDetailsClick={() =>
                                                logClick(
                                                    `${tabName} historical details ${metric.displayName}`,
                                                )
                                            }
                                        />
                                    ),
                                )}
                            </div>
                        </div>
                    ))
                )}
            </Grid.Item>
        </>
    )
}
